import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase.js';

// AiUsageWidget — Task #105.
//
// Renders inside admin Settings → AI add-on section when the add-on is enabled.
// Real-time subscribed to vendors/{vid}/aiUsage/{yyyy-mm}, shows:
//   - customer triages used vs cap
//   - responder queries used vs cap
//   - estimated USD spend this month
//
// Caps mirror functions/src/ai-usage.js HARD_CAPS — keep these in sync.

const HARD_CAPS = {
  customerTriages: 250,
  responderQueries: 500,
};

function yyyymm(date = new Date()) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

export default function AiUsageWidget({ vendorId }) {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    const period = yyyymm();
    const unsub = onSnapshot(
      doc(db, 'vendors', vendorId, 'aiUsage', period),
      (snap) => setUsage(snap.exists() ? snap.data() : { empty: true }),
      () => setUsage({ error: true }),
    );
    return () => unsub();
  }, [vendorId]);

  if (!usage) {
    return (
      <div style={loadingStyle}>Loading usage…</div>
    );
  }
  if (usage.error) {
    return (
      <div style={{ ...loadingStyle, color: '#991b1b' }}>
        Couldn't load this month's usage.
      </div>
    );
  }

  const triages = usage.customerTriages || 0;
  const queries = usage.responderQueries || 0;
  const cost = usage.totalCostUsd || 0;

  return (
    <div style={{
      marginTop: 12, padding: 12,
      background: 'white', borderRadius: 8,
      border: '1px solid var(--border)',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 10,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>This month's AI usage</div>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          {yyyymm()}
        </div>
      </div>

      <Meter
        label="Requester triages"
        used={triages}
        cap={HARD_CAPS.customerTriages}
      />
      <Meter
        label="Responder queries"
        used={queries}
        cap={HARD_CAPS.responderQueries}
      />

      <div style={{
        marginTop: 10, paddingTop: 10,
        borderTop: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      }}>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Est. API cost this month</span>
        <span style={{ fontSize: 13, fontWeight: 600 }}>${cost.toFixed(2)} USD</span>
      </div>

      <p style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)' }}>
        Caps reset on the 1st of each month. Hit a cap and need it raised? Email
        support — we'll handle it. The $15 flat add-on already covers typical use;
        this is purely a runaway-cost guard for us.
      </p>
    </div>
  );
}

function Meter({ label, used, cap }) {
  const pct = Math.min(100, Math.round((used / cap) * 100));
  let barColor = '#10b981';        // green
  let textColor = 'inherit';
  if (pct >= 100) { barColor = '#dc2626'; textColor = '#991b1b'; }   // red
  else if (pct >= 80) { barColor = '#f59e0b'; textColor = '#92400e'; } // yellow

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: 12, marginBottom: 4,
      }}>
        <span>{label}</span>
        <span style={{ color: textColor, fontWeight: pct >= 80 ? 600 : 400 }}>
          {used} / {cap}
        </span>
      </div>
      <div style={{
        height: 6, borderRadius: 3,
        background: '#e5e7eb', overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: barColor,
          transition: 'width 250ms ease',
        }} />
      </div>
    </div>
  );
}

const loadingStyle = {
  marginTop: 12, padding: 12,
  background: '#f8fafc', borderRadius: 8,
  fontSize: 13, color: 'var(--muted)',
};
